import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import {
  Database, Users, FlaskConical, Workflow, Trash2, RefreshCw,
  AlertTriangle, CheckCircle2, Shield, Building2, ArrowLeft,
  Zap, X, Info, Globe, Star, ChevronRight, BarChart3,
  HardDrive, Download, ServerCrash, Clock, Upload,
} from 'lucide-react';
import { hasPhpBackend, getApiBase, apiAdminCreateTasmanDemo } from '@/lib/phpApi';
import { buildRegulatoryReportNumber } from '@/lib/pdfGenerator';
import PageHeader from '@/components/PageHeader';

// ─── DB Dataset type ──────────────────────────────────────────────────────────
interface DbDataset {
  slug: string;
  name: string;
  description: string;
  customerCount: number;
  sampleCount: number;
  trainCount: number;
  region: string;
  fileExists: boolean;
  importedCount: number;
  lastImportedAt: string | null;
}

// ─── Types ────────────────────────────────────────────────────────────────────
interface Customer {
  id: string; name: string; company: string; contactEmail: string;
  phone: string; address: string; notes: string; createdAt: string;
  country?: string; industryType?: string; standard?: string; isDemo?: boolean;
}
interface Sample {
  id: number; customerId: number | null; userId: number;
  sampleName: string; reportNumber: string | null;
  params: Record<string, number>; standard: string;
  sampleType: string; createdAt: string; updatedAt: string;
  _local?: boolean; _demo?: boolean; notes?: string | null;
  linkedSampleId?: number | null;
}
interface Train {
  id: number; userId: number; customerId?: number; name: string;
  description: string | null; sampleId: number | null;
  equipmentList: { key: string; label: string; category: string; shortLabel: string }[];
  status: string; assessmentResult: any;
  createdAt: string; updatedAt: string; _local?: boolean; _demo?: boolean;
}

// ─── Storage keys ─────────────────────────────────────────────────────────────
const LS = {
  customers: 'rb_customers',
  influent:  'rb_influent_samples_v2',
  legacy:    'rb_customer_samples',
  trains:    'rb_process_trains_v2',
  proposals: 'rb_proposals_v2',
  session:   'rb_session_v2',
};

function rd<T>(key: string): T[] { try { return JSON.parse(localStorage.getItem(key) || '[]'); } catch { return []; } }
function wr(key: string, d: any[]) { localStorage.setItem(key, JSON.stringify(d)); }

// ─── Demo Customer Data ───────────────────────────────────────────────────────
const COUNTRY_FLAGS: Record<string, string> = {
  AU:'🇦🇺', NZ:'🇳🇿', GB:'🇬🇧', IE:'🇮🇪', DE:'🇩🇪', FR:'🇫🇷', SG:'🇸🇬', US:'🇺🇸', OTHER:'🌐',
};
const STANDARD_COLORS: Record<string, string> = {
  UK_EA: 'text-blue-400', AU_EPA: 'text-emerald-400', EU_UWWTD: 'text-violet-400',
};

const DEMO_CUSTOMERS: Customer[] = [
  { id:'1001', name:'John Windsor',    company:'Windsor Dairy Ltd',              contactEmail:'john@windsor.uk',                 phone:'+44 1753 123456', address:'Windsor, Berkshire, UK',                   notes:'Dairy processing — CAT-2 trade waste discharge to sewer.',                              createdAt:'2026-01-01T00:00:00Z', country:'GB', industryType:'Dairy Processing',          standard:'UK_EA',    isDemo:true },
  { id:'1002', name:'Sarah Thames',    company:'Thames Brewing Co.',             contactEmail:'sarah@thamesbrew.co.uk',          phone:'+44 20 7946 0958',address:'London, UK',                               notes:'High-COD brewery effluent, seasonal variation.',                                        createdAt:'2026-01-01T00:00:00Z', country:'GB', industryType:'Brewery / Distillery',      standard:'UK_EA',    isDemo:true },
  { id:'1003', name:'Mark Sheffield',  company:'Sheffield Metalworks',           contactEmail:'mark@sheffieldmetal.co.uk',       phone:'+44 114 496 0123',address:'Sheffield, South Yorkshire, UK',           notes:'Heavy metals — List I substances (Cr, Pb). EP permit required.',                        createdAt:'2026-01-01T00:00:00Z', country:'GB', industryType:'Metal Finishing',           standard:'UK_EA',    isDemo:true },
  { id:'1004', name:'Emma Bristol',    company:'Bristol Textile Group',          contactEmail:'emma@bristoltex.com.au',          phone:'+61 2 9374 4000', address:'Bristol, NSW, Australia',                  notes:'Textile dyeing effluent — colour, COD primary concern.',                                createdAt:'2026-01-01T00:00:00Z', country:'AU', industryType:'Textile / Laundry',          standard:'AU_EPA',   isDemo:true },
  { id:'1005', name:'James Aberdeen',  company:'Aberdeen Seafood Processors',    contactEmail:'james@aberdeenseafood.co.uk',     phone:'+44 1224 785000', address:'Aberdeen, Scotland',                        notes:'High FOG, blood washdown, ammonia spikes.',                                             createdAt:'2026-01-01T00:00:00Z', country:'GB', industryType:'Seafood Processing',        standard:'UK_EA',    isDemo:true },
  { id:'1006', name:'Tom MacGregor',   company:'Sydney Meats Pty Ltd',           contactEmail:'t.macgregor@sydneymeats.com.au', phone:'+61 2 9745 8800', address:'Homebush, NSW, Australia',                 notes:'Cattle and sheep abattoir. Very high BOD/FOG from blood and gut washdown.',             createdAt:'2026-01-02T00:00:00Z', country:'AU', industryType:'Slaughterhouse / Abattoir', standard:'AU_EPA',   isDemo:true },
  { id:'1007', name:'Klaus Richter',   company:'Munich Chemical GmbH',           contactEmail:'k.richter@munchcm.de',            phone:'+49 89 4521 6700',address:'Freimann, Munich, Germany',                notes:'Batch chemical synthesis. Variable pH and high COD. EU IED permit holder.',             createdAt:'2026-01-02T00:00:00Z', country:'DE', industryType:'Chemical Manufacturing',    standard:'EU_UWWTD', isDemo:true },
  { id:'1008', name:'Helen Marsh',     company:'Leeds Food Products Ltd',        contactEmail:'h.marsh@leedsfood.co.uk',         phone:'+44 113 248 5200',address:'Kirkstall, Leeds, West Yorkshire',        notes:'Mixed food processing — sauces and ready meals. Seasonal peaks.',                       createdAt:'2026-01-02T00:00:00Z', country:'GB', industryType:'Food Processing',           standard:'UK_EA',    isDemo:true },
  { id:'1009', name:'Peter Ngata',     company:'Auckland Dairy Partners Ltd',    contactEmail:'p.ngata@adairy.co.nz',            phone:'+64 9 835 4200', address:'Wiri, Auckland, New Zealand',               notes:'Dairy co-operative. Milk processing and cheese production effluent.',                   createdAt:'2026-01-02T00:00:00Z', country:'NZ', industryType:'Dairy Processing',          standard:'AU_EPA',   isDemo:true },
  { id:'1010', name:'Abi Clarke',      company:'Melbourne Craft Brewers Pty',    contactEmail:'a.clarke@melbcraft.com.au',       phone:'+61 3 9429 7600', address:'Richmond, Melbourne, Victoria',            notes:'Craft brewery with distillery. COD-heavy spent grain and yeast effluent.',              createdAt:'2026-01-02T00:00:00Z', country:'AU', industryType:'Brewery / Distillery',      standard:'AU_EPA',   isDemo:true },
  { id:'1011', name:'Hamish Grant',    company:'Glasgow Single Malt Ltd',        contactEmail:'h.grant@glasgowmalt.co.uk',       phone:'+44 141 552 3900',address:'Port Dundas, Glasgow, Scotland',           notes:'Malt whisky distillery. Pot ale and spent lees — extreme COD (>20,000 mg/L).',         createdAt:'2026-01-03T00:00:00Z', country:'GB', industryType:'Brewery / Distillery',      standard:'UK_EA',    isDemo:true },
  { id:'1012', name:'Rhiannon Thomas', company:'Cardiff Poultry Processing Ltd', contactEmail:'r.thomas@cardiffpoultry.co.uk',  phone:'+44 29 2048 7200',address:'Treforest Industrial Estate, Pontypridd',  notes:'Broiler processing — scalding, defeathering, evisceration washdown.',                   createdAt:'2026-01-03T00:00:00Z', country:'GB', industryType:'Slaughterhouse / Abattoir', standard:'UK_EA',    isDemo:true },
  { id:'1013', name:'Gavin Hunter',    company:'Perth Mineral Extraction Corp',  contactEmail:'g.hunter@perthmining.com.au',    phone:'+61 8 6213 4500', address:'Kalgoorlie-Boulder, WA, Australia',         notes:'Open-cut gold and nickel mine. Acid mine drainage — low pH, high Fe/Mn/Zn.',           createdAt:'2026-01-03T00:00:00Z', country:'AU', industryType:'Mining / Extractive',       standard:'AU_EPA',   isDemo:true },
  { id:'1014', name:'Siobhan Kelly',   company:'Dublin Hotel and Spa Group',     contactEmail:'s.kelly@dublinhotelgroup.ie',     phone:'+353 1 677 8200', address:'St Stephens Green, Dublin 2, Ireland',     notes:'Luxury hotel — kitchen grease, laundry and spa wastewater combined.',                   createdAt:'2026-01-03T00:00:00Z', country:'IE', industryType:'Hospitality / Catering',    standard:'UK_EA',    isDemo:true },
  { id:'1015', name:'Dr Wei Lin',      company:'Singapore BioTech Pte Ltd',      contactEmail:'w.lin@sgbiotech.com.sg',          phone:'+65 6779 8800', address:'Tuas Biomedical Park, Singapore',            notes:'Biopharmaceutical fermentation. Complex organics, solvents, API residuals.',           createdAt:'2026-01-03T00:00:00Z', country:'SG', industryType:'Pharmaceutical',            standard:'UK_EA',    isDemo:true },
  { id:'1016', name:'Jacques Moreau',  company:'Toulouse Food Ingredients SA',   contactEmail:'j.moreau@tfi-sa.fr',              phone:'+33 5 6143 8900', address:'Zone Industrielle, Toulouse, France',      notes:'Starch and hydrolysate production. High BOD/COD from fermentation and extraction.',    createdAt:'2026-01-04T00:00:00Z', country:'FR', industryType:'Food Processing',           standard:'EU_UWWTD', isDemo:true },
  { id:'1017', name:'Karl Bauer',      company:'Frankfurt Precision Coatings GmbH',contactEmail:'k.bauer@fpc-gmbh.de',          phone:'+49 69 9542 3100',address:'Hoechst Industrial Park, Frankfurt, Germany',notes:'Electroplating and surface treatment. Chromate, nickel, zinc process baths.',        createdAt:'2026-01-04T00:00:00Z', country:'DE', industryType:'Metal Finishing',           standard:'EU_UWWTD', isDemo:true },
  { id:'1018', name:'Sandra Wu',       company:'Brisbane Cold Stores Pty Ltd',   contactEmail:'s.wu@brisbanecold.com.au',        phone:'+61 7 3890 5600', address:'Hemmant, Brisbane, Queensland',             notes:'Cold storage and blast freezing. Refrigeration, cleaning and defrost effluent.',       createdAt:'2026-01-04T00:00:00Z', country:'AU', industryType:'Food Processing',           standard:'AU_EPA',   isDemo:true },
  { id:'1019', name:'Derek Olsen',     company:'Northampton Rendering Co Ltd',   contactEmail:'d.olsen@northantsrender.co.uk',  phone:'+44 1604 582 900',address:'Brackmills Industrial Estate, Northampton', notes:'Animal by-products rendering. Press liquor — exceptionally high COD/FOG.',            createdAt:'2026-01-04T00:00:00Z', country:'GB', industryType:'Slaughterhouse / Abattoir', standard:'UK_EA',    isDemo:true },
  { id:'1020', name:'Marcus Webb',     company:'East London Water Authority',    contactEmail:'m.webb@elwa.gov.uk',              phone:'+44 20 8534 7700',address:'Stratford, London E15',                     notes:'Combined municipal and light industrial catchment. Seasonal wet weather peaks.',        createdAt:'2026-01-04T00:00:00Z', country:'GB', industryType:'Municipal / Council',       standard:'UK_EA',    isDemo:true },
];

// Helper — build a demo sample compactly
// rn parameter kept for call-site compat but ignored — regulatory number is derived from std+type
function ds(id:number,cid:number,name:string,rn:string,std:string,type:string,p:Record<string,number>,dt:string): Sample {
  const reportType = type === 'effluent' ? 'EFF' : 'INF';
  const reportNumber = buildRegulatoryReportNumber(reportType, std);
  return { id, customerId: cid, userId: 1, sampleName: name, reportNumber, standard: std, sampleType: type, params: p, notes: null, linkedSampleId: null, createdAt: dt, updatedAt: dt, _local: true, _demo: true };
}

// 60 demo samples — 3 per customer (influent / effluent / variation)
const DEMO_SAMPLES: Sample[] = [
  // ─ 1001 Windsor Dairy (UK_EA Dairy)
  ds(6001,1001,'Windsor Dairy — Peak Summer Influent','RB-IN-2026-0006','UK_EA','influent',{ph:5.8,temperature:24,cod:9200,bod:3800,tss:780,nh3:48,og:145,flowRate:920,tds:2400,conductivity:3600,lead:0.05,chromium:0.02},'2026-03-15T07:30:00Z'),
  ds(6002,1001,'Windsor Dairy — Treated Effluent','RB-EFF-2026-0007','UK_EA','effluent',{ph:7.3,temperature:17,cod:98,bod:19,tss:26,nh3:5.8,og:8.2,flowRate:880,tds:980,conductivity:1470,lead:0.01,chromium:0.01},'2026-03-15T16:00:00Z'),
  ds(6003,1001,'Windsor Dairy — CIP Cleaning Discharge','RB-IN-2026-0008','UK_EA','influent',{ph:10.2,temperature:38,cod:4100,bod:1800,tss:420,nh3:22,og:95,flowRate:280,tds:3200,conductivity:4800,lead:0.03,chromium:0.01},'2026-03-16T02:00:00Z'),
  // ─ 1002 Thames Brewing (UK_EA Brewery)
  ds(6004,1002,'Thames Brewing — Weekly Composite','RB-IN-2026-0009','UK_EA','influent',{ph:4.7,temperature:19,cod:12500,bod:5100,tss:410,nh3:26,og:16,flowRate:520,tds:1350,conductivity:2025,lead:0.02,chromium:0.01},'2026-02-10T09:00:00Z'),
  ds(6005,1002,'Thames Brewing — Post-Treatment Effluent','RB-EFF-2026-0010','UK_EA','effluent',{ph:7.5,temperature:16,cod:108,bod:21,tss:29,nh3:7.2,og:9.5,flowRate:495,tds:680,conductivity:1020,lead:0.01,chromium:0.01},'2026-02-10T17:00:00Z'),
  ds(6006,1002,'Thames Brewing — October Seasonal Peak','RB-IN-2026-0011','UK_EA','influent',{ph:4.3,temperature:21,cod:16800,bod:6900,tss:550,nh3:35,og:22,flowRate:680,tds:1800,conductivity:2700,lead:0.02,chromium:0.01},'2026-02-20T09:00:00Z'),
  // ─ 1003 Sheffield Metalworks (UK_EA Metal Finishing)
  ds(6007,1003,'Sheffield Metalworks — Process Effluent','RB-IN-2026-0012','UK_EA','influent',{ph:3.1,temperature:20,cod:480,bod:85,tss:310,nh3:8,og:35,flowRate:85,tds:4800,conductivity:7200,lead:3.2,chromium:9.4},'2026-01-28T10:00:00Z'),
  ds(6008,1003,'Sheffield Metalworks — Post-Treatment','RB-EFF-2026-0013','UK_EA','effluent',{ph:7.8,temperature:18,cod:72,bod:13,tss:24,nh3:2.1,og:5.5,flowRate:82,tds:1200,conductivity:1800,lead:0.06,chromium:0.08},'2026-01-28T18:00:00Z'),
  ds(6009,1003,'Sheffield Metalworks — High-Metal Event','RB-IN-2026-0014','UK_EA','influent',{ph:2.8,temperature:22,cod:680,bod:95,tss:520,nh3:5,og:28,flowRate:65,tds:6200,conductivity:9300,lead:6.8,chromium:18.5},'2026-02-14T11:00:00Z'),
  // ─ 1004 Bristol Textile (AU_EPA Textile)
  ds(6010,1004,'Bristol Textile — Dye Batch Influent','RB-IN-2026-0015','AU_EPA','influent',{ph:10.5,temperature:38,cod:3200,bod:520,tss:720,nh3:12,og:18,flowRate:1350,tds:5200,conductivity:7800,lead:0.08,chromium:0.25},'2026-02-05T08:00:00Z'),
  ds(6011,1004,'Bristol Textile — Treated Effluent','RB-EFF-2026-0016','AU_EPA','effluent',{ph:7.8,temperature:22,cod:265,bod:43,tss:52,nh3:8.5,og:14.2,flowRate:1290,tds:1800,conductivity:2700,lead:0.02,chromium:0.05},'2026-02-05T19:00:00Z'),
  ds(6012,1004,'Bristol Textile — Peak Dye Production','RB-IN-2026-0017','AU_EPA','influent',{ph:11.2,temperature:42,cod:5800,bod:890,tss:1250,nh3:18,og:25,flowRate:950,tds:7400,conductivity:11100,lead:0.12,chromium:0.45},'2026-02-25T08:30:00Z'),
  // ─ 1005 Aberdeen Seafood (UK_EA Seafood)
  ds(6013,1005,'Aberdeen Seafood — Composite Influent','RB-IN-2026-0018','UK_EA','influent',{ph:7.2,temperature:12,cod:7200,bod:2950,tss:780,nh3:88,og:450,flowRate:720,tds:2100,conductivity:3150,lead:0.15,chromium:0.08},'2026-02-18T08:00:00Z'),
  ds(6014,1005,'Aberdeen Seafood — Final Effluent','RB-EFF-2026-0019','UK_EA','effluent',{ph:7.4,temperature:14,cod:115,bod:22,tss:31,nh3:8.5,og:11.2,flowRate:690,tds:850,conductivity:1275,lead:0.03,chromium:0.02},'2026-02-18T17:30:00Z'),
  ds(6015,1005,'Aberdeen Seafood — Peak Harvest Season','RB-IN-2026-0020','UK_EA','influent',{ph:7.0,temperature:10,cod:9500,bod:3900,tss:1100,nh3:120,og:680,flowRate:1050,tds:2800,conductivity:4200,lead:0.22,chromium:0.10},'2026-03-01T07:00:00Z'),
  // ─ 1006 Sydney Meats (AU_EPA Abattoir)
  ds(6016,1006,'Sydney Meats — Daily Composite Influent','RB-IN-2026-0021','AU_EPA','influent',{ph:7.0,temperature:22,cod:5200,bod:2500,tss:1950,nh3:95,og:720,flowRate:450,tds:2400,conductivity:3600,lead:0.08,chromium:0.04},'2026-01-08T08:00:00Z'),
  ds(6017,1006,'Sydney Meats — Treated Final Effluent','RB-EFF-2026-0022','AU_EPA','effluent',{ph:7.3,temperature:18,cod:248,bod:46,tss:58,nh3:42,og:18.5,flowRate:420,tds:980,conductivity:1470,lead:0.02,chromium:0.01},'2026-01-08T19:00:00Z'),
  ds(6018,1006,'Sydney Meats — Peak Kill Day','RB-IN-2026-0023','AU_EPA','influent',{ph:6.8,temperature:25,cod:8500,bod:4100,tss:3200,nh3:145,og:1150,flowRate:580,tds:3200,conductivity:4800,lead:0.12,chromium:0.06},'2026-01-22T07:30:00Z'),
  // ─ 1007 Munich Chemical (EU_UWWTD Chemical)
  ds(6019,1007,'Munich Chemical — Process Stream','RB-IN-2026-0024','EU_UWWTD','influent',{ph:4.2,temperature:32,cod:4250,bod:980,tss:310,nh3:22,og:42,flowRate:220,tds:3800,conductivity:5700,lead:0.15,chromium:0.45},'2026-01-12T09:00:00Z'),
  ds(6020,1007,'Munich Chemical — Treated Effluent','RB-EFF-2026-0025','EU_UWWTD','effluent',{ph:7.2,temperature:20,cod:112,bod:21,tss:28,nh3:9.5,og:8.2,flowRate:205,tds:950,conductivity:1425,lead:0.05,chromium:0.08},'2026-01-12T20:00:00Z'),
  ds(6021,1007,'Munich Chemical — Batch Process Discharge','RB-IN-2026-0026','EU_UWWTD','influent',{ph:3.5,temperature:28,cod:8900,bod:2200,tss:520,nh3:38,og:65,flowRate:180,tds:5400,conductivity:8100,lead:0.28,chromium:0.92},'2026-02-08T14:00:00Z'),
  // ─ 1008 Leeds Food (UK_EA Food Processing)
  ds(6022,1008,'Leeds Food — Daily Composite Influent','RB-IN-2026-0027','UK_EA','influent',{ph:6.5,temperature:22,cod:4800,bod:2100,tss:520,nh3:32,og:110,flowRate:385,tds:1650,conductivity:2475,lead:0.03,chromium:0.02},'2026-01-15T08:00:00Z'),
  ds(6023,1008,'Leeds Food — Treated Final Effluent','RB-EFF-2026-0028','UK_EA','effluent',{ph:7.2,temperature:16,cod:105,bod:20,tss:28,nh3:6.8,og:9.8,flowRate:365,tds:780,conductivity:1170,lead:0.01,chromium:0.01},'2026-01-15T17:30:00Z'),
  ds(6024,1008,'Leeds Food — Christmas Peak Influent','RB-IN-2026-0029','UK_EA','influent',{ph:6.2,temperature:24,cod:7200,bod:3100,tss:820,nh3:48,og:175,flowRate:450,tds:2250,conductivity:3375,lead:0.04,chromium:0.02},'2026-01-29T09:00:00Z'),
  // ─ 1009 Auckland Dairy (AU_EPA Dairy)
  ds(6025,1009,'Auckland Dairy — Influent Q1','RB-IN-2026-0030','AU_EPA','influent',{ph:6.3,temperature:23,cod:8100,bod:3300,tss:650,nh3:42,og:135,flowRate:780,tds:2100,conductivity:3150,lead:0.04,chromium:0.02},'2026-01-20T08:00:00Z'),
  ds(6026,1009,'Auckland Dairy — Treated Effluent','RB-EFF-2026-0031','AU_EPA','effluent',{ph:7.4,temperature:16,cod:165,bod:32,tss:48,nh3:22,og:15.5,flowRate:750,tds:1050,conductivity:1575,lead:0.01,chromium:0.01},'2026-01-20T18:00:00Z'),
  ds(6027,1009,'Auckland Dairy — Spring Peak Season','RB-IN-2026-0032','AU_EPA','influent',{ph:6.0,temperature:25,cod:11500,bod:4700,tss:890,nh3:58,og:182,flowRate:950,tds:2900,conductivity:4350,lead:0.06,chromium:0.03},'2026-03-10T07:30:00Z'),
  // ─ 1010 Melbourne Brewers (AU_EPA Brewery)
  ds(6028,1010,'Melbourne Brewers — Composite Influent','RB-IN-2026-0033','AU_EPA','influent',{ph:4.8,temperature:20,cod:9800,bod:3950,tss:340,nh3:21,og:13,flowRate:480,tds:1180,conductivity:1770,lead:0.02,chromium:0.01},'2026-02-03T08:30:00Z'),
  ds(6029,1010,'Melbourne Brewers — Treated Effluent','RB-EFF-2026-0034','AU_EPA','effluent',{ph:7.6,temperature:17,cod:185,bod:38,tss:45,nh3:18,og:14.2,flowRate:455,tds:750,conductivity:1125,lead:0.01,chromium:0.01},'2026-02-03T19:30:00Z'),
  ds(6030,1010,'Melbourne Brewers — Peak Production Batch','RB-IN-2026-0035','AU_EPA','influent',{ph:4.5,temperature:22,cod:14500,bod:5800,tss:480,nh3:30,og:18,flowRate:620,tds:1580,conductivity:2370,lead:0.02,chromium:0.01},'2026-02-24T08:00:00Z'),
  // ─ 1011 Glasgow Single Malt (UK_EA Distillery)
  ds(6031,1011,'Glasgow Single Malt — Pot Ale Influent','RB-IN-2026-0036','UK_EA','influent',{ph:4.2,temperature:26,cod:22000,bod:9500,tss:420,nh3:18,og:8,flowRate:95,tds:8500,conductivity:12750,lead:0.02,chromium:0.01},'2026-01-14T09:00:00Z'),
  ds(6032,1011,'Glasgow Single Malt — Treated Effluent','RB-EFF-2026-0037','UK_EA','effluent',{ph:7.6,temperature:18,cod:98,bod:18,tss:24,nh3:6.2,og:5.5,flowRate:90,tds:680,conductivity:1020,lead:0.01,chromium:0.01},'2026-01-14T20:00:00Z'),
  ds(6033,1011,'Glasgow Single Malt — Still Condensate','RB-IN-2026-0038','UK_EA','influent',{ph:5.8,temperature:28,cod:3800,bod:1600,tss:180,nh3:8,og:5,flowRate:120,tds:1200,conductivity:1800,lead:0.01,chromium:0.01},'2026-02-11T11:00:00Z'),
  // ─ 1012 Cardiff Poultry (UK_EA Abattoir)
  ds(6034,1012,'Cardiff Poultry — Composite Influent','RB-IN-2026-0039','UK_EA','influent',{ph:7.1,temperature:20,cod:3800,bod:1750,tss:1350,nh3:72,og:385,flowRate:380,tds:2200,conductivity:3300,lead:0.06,chromium:0.03},'2026-01-19T08:00:00Z'),
  ds(6035,1012,'Cardiff Poultry — Treated Final Effluent','RB-EFF-2026-0040','UK_EA','effluent',{ph:7.3,temperature:16,cod:105,bod:20,tss:29,nh3:8.2,og:11.5,flowRate:355,tds:890,conductivity:1335,lead:0.02,chromium:0.01},'2026-01-19T18:30:00Z'),
  ds(6036,1012,'Cardiff Poultry — Scalding Line Discharge','RB-IN-2026-0041','UK_EA','influent',{ph:7.5,temperature:62,cod:5500,bod:2600,tss:2100,nh3:95,og:520,flowRate:280,tds:2900,conductivity:4350,lead:0.08,chromium:0.04},'2026-02-06T13:00:00Z'),
  // ─ 1013 Perth Mining (AU_EPA AMD)
  ds(6037,1013,'Perth Mining — Acid Mine Drainage','RB-IN-2026-0042','AU_EPA','influent',{ph:2.7,temperature:18,cod:180,bod:18,tss:920,nh3:2,og:5,flowRate:1200,tds:12500,conductivity:18750,lead:0.45,chromium:0.12,iron:195,manganese:48,copper:9.2,zinc:28,sulphate:4800},'2026-01-16T09:00:00Z'),
  ds(6038,1013,'Perth Mining — Treated Effluent','RB-EFF-2026-0043','AU_EPA','effluent',{ph:7.8,temperature:17,cod:45,bod:5,tss:32,nh3:1.2,og:2.5,flowRate:1150,tds:2800,conductivity:4200,lead:0.05,chromium:0.04,iron:0.8,manganese:0.4,copper:0.04,zinc:0.15,sulphate:850},'2026-01-16T19:00:00Z'),
  ds(6039,1013,'Perth Mining — Wet Season Inflow Event','RB-IN-2026-0044','AU_EPA','influent',{ph:4.2,temperature:15,cod:95,bod:8,tss:1800,nh3:1.5,og:3,flowRate:2400,tds:5200,conductivity:7800,lead:0.28,chromium:0.08,iron:85,manganese:22,copper:3.8,zinc:12,sulphate:2200},'2026-02-28T06:00:00Z'),
  // ─ 1014 Dublin Hotel (UK_EA Hospitality)
  ds(6040,1014,'Dublin Hotel — Combined Kitchen Influent','RB-IN-2026-0045','UK_EA','influent',{ph:6.9,temperature:26,cod:1450,bod:620,tss:380,nh3:38,og:295,flowRate:185,tds:850,conductivity:1275,lead:0.02,chromium:0.01},'2026-01-22T09:00:00Z'),
  ds(6041,1014,'Dublin Hotel — Treated Effluent','RB-EFF-2026-0046','UK_EA','effluent',{ph:7.3,temperature:18,cod:95,bod:18,tss:25,nh3:7.5,og:10.5,flowRate:175,tds:420,conductivity:630,lead:0.01,chromium:0.01},'2026-01-22T17:30:00Z'),
  ds(6042,1014,'Dublin Hotel — Weekend Events Peak','RB-IN-2026-0047','UK_EA','influent',{ph:6.5,temperature:28,cod:2800,bod:1200,tss:620,nh3:58,og:480,flowRate:250,tds:1250,conductivity:1875,lead:0.03,chromium:0.01},'2026-02-15T22:00:00Z'),
  // ─ 1015 Singapore BioTech (UK_EA Pharma)
  ds(6043,1015,'Singapore BioTech — Fermentation Effluent','RB-IN-2026-0048','UK_EA','influent',{ph:7.5,temperature:24,cod:3200,bod:1400,tss:195,nh3:28,og:22,flowRate:145,tds:1580,conductivity:2370,lead:0.04,chromium:0.03},'2026-01-25T09:00:00Z'),
  ds(6044,1015,'Singapore BioTech — Treated Effluent','RB-EFF-2026-0049','UK_EA','effluent',{ph:7.2,temperature:19,cod:102,bod:19,tss:22,nh3:5.5,og:6.8,flowRate:138,tds:680,conductivity:1020,lead:0.01,chromium:0.01},'2026-01-25T20:00:00Z'),
  ds(6045,1015,'Singapore BioTech — High-Load Ferment Batch','RB-IN-2026-0050','UK_EA','influent',{ph:5.8,temperature:30,cod:8500,bod:3800,tss:420,nh3:45,og:35,flowRate:95,tds:3200,conductivity:4800,lead:0.06,chromium:0.04},'2026-02-22T08:00:00Z'),
  // ─ 1016 Toulouse Food (EU_UWWTD Food)
  ds(6046,1016,'Toulouse Food — Starch Process Influent','RB-IN-2026-0051','EU_UWWTD','influent',{ph:5.9,temperature:23,cod:5800,bod:2500,tss:610,nh3:38,og:130,flowRate:320,tds:1950,conductivity:2925,lead:0.03,chromium:0.02},'2026-01-26T08:00:00Z'),
  ds(6047,1016,'Toulouse Food — Treated Effluent','RB-EFF-2026-0052','EU_UWWTD','effluent',{ph:7.3,temperature:17,cod:115,bod:22,tss:28,nh3:8.5,og:9.5,flowRate:305,tds:780,conductivity:1170,lead:0.01,chromium:0.01},'2026-01-26T19:30:00Z'),
  ds(6048,1016,'Toulouse Food — Peak Extraction Load','RB-IN-2026-0053','EU_UWWTD','influent',{ph:5.5,temperature:28,cod:9200,bod:4100,tss:950,nh3:55,og:165,flowRate:420,tds:2800,conductivity:4200,lead:0.05,chromium:0.03},'2026-02-16T09:30:00Z'),
  // ─ 1017 Frankfurt Coatings (EU_UWWTD Metal)
  ds(6049,1017,'Frankfurt Coatings — Mixed Process','RB-IN-2026-0054','EU_UWWTD','influent',{ph:3.5,temperature:22,cod:550,bod:95,tss:380,nh3:12,og:42,flowRate:68,tds:5200,conductivity:7800,lead:4.8,chromium:12.5},'2026-01-28T09:00:00Z'),
  ds(6050,1017,'Frankfurt Coatings — Post-Treatment','RB-EFF-2026-0055','EU_UWWTD','effluent',{ph:7.8,temperature:19,cod:78,bod:14,tss:26,nh3:3.2,og:6.5,flowRate:65,tds:1150,conductivity:1725,lead:0.07,chromium:0.09},'2026-01-28T18:30:00Z'),
  ds(6051,1017,'Frankfurt Coatings — Chromate Bath Dump','RB-IN-2026-0056','EU_UWWTD','influent',{ph:2.2,temperature:25,cod:920,bod:120,tss:680,nh3:8,og:28,flowRate:42,tds:8500,conductivity:12750,lead:8.5,chromium:45.2},'2026-02-19T10:00:00Z'),
  // ─ 1018 Brisbane Cold Stores (AU_EPA Food)
  ds(6052,1018,'Brisbane Cold Stores — Cleaning Influent','RB-IN-2026-0057','AU_EPA','influent',{ph:7.2,temperature:20,cod:2800,bod:1200,tss:320,nh3:18,og:85,flowRate:285,tds:980,conductivity:1470,lead:0.02,chromium:0.01},'2026-02-02T08:00:00Z'),
  ds(6053,1018,'Brisbane Cold Stores — Treated Effluent','RB-EFF-2026-0058','AU_EPA','effluent',{ph:7.4,temperature:16,cod:182,bod:35,tss:42,nh3:12.5,og:14.8,flowRate:270,tds:520,conductivity:780,lead:0.01,chromium:0.01},'2026-02-02T18:00:00Z'),
  ds(6054,1018,'Brisbane Cold Stores — Ammonia Spill Event','RB-IN-2026-0059','AU_EPA','influent',{ph:8.5,temperature:18,cod:950,bod:380,tss:185,nh3:385,og:25,flowRate:320,tds:650,conductivity:975,lead:0.02,chromium:0.01},'2026-03-18T03:30:00Z'),
  // ─ 1019 Northampton Rendering (UK_EA Rendering)
  ds(6055,1019,'Northampton Rendering — Press Liquor','RB-IN-2026-0060','UK_EA','influent',{ph:7.8,temperature:45,cod:32000,bod:14500,tss:4200,nh3:320,og:3800,flowRate:95,tds:8500,conductivity:12750,lead:0.12,chromium:0.05},'2026-01-10T08:00:00Z'),
  ds(6056,1019,'Northampton Rendering — Treated Effluent','RB-EFF-2026-0061','UK_EA','effluent',{ph:7.4,temperature:20,cod:118,bod:23,tss:32,nh3:9.2,og:13.5,flowRate:85,tds:1050,conductivity:1575,lead:0.03,chromium:0.01},'2026-01-10T20:00:00Z'),
  ds(6057,1019,'Northampton Rendering — Steam Condensate','RB-IN-2026-0062','UK_EA','influent',{ph:7.5,temperature:35,cod:6500,bod:2900,tss:850,nh3:85,og:620,flowRate:125,tds:2800,conductivity:4200,lead:0.06,chromium:0.02},'2026-02-10T14:00:00Z'),
  // ─ 1020 East London WW (UK_EA Municipal)
  ds(6058,1020,'East London WW — Dry Weather Flow','RB-IN-2026-0063','UK_EA','influent',{ph:7.3,temperature:16,cod:485,bod:245,tss:295,nh3:48,og:35,flowRate:8500,tds:620,conductivity:930,lead:0.05,chromium:0.02},'2026-01-13T09:00:00Z'),
  ds(6059,1020,'East London WW — Treated Final Effluent','RB-EFF-2026-0064','UK_EA','effluent',{ph:7.5,temperature:14,cod:42,bod:10,tss:16,nh3:1.8,og:3.5,flowRate:8200,tds:285,conductivity:428,lead:0.01,chromium:0.01},'2026-01-13T18:30:00Z'),
  ds(6060,1020,'East London WW — Wet Weather Storm Event','RB-IN-2026-0065','UK_EA','influent',{ph:7.0,temperature:12,cod:280,bod:145,tss:520,nh3:28,og:22,flowRate:15800,tds:380,conductivity:570,lead:0.03,chromium:0.01},'2026-02-26T14:30:00Z'),
];

const EQ = [
  {key:'screening',label:'Screening',category:'pretreatment',shortLabel:'Screen'},
  {key:'eq',label:'Equalization Tank',category:'pretreatment',shortLabel:'EQ Tank'},
  {key:'ph',label:'pH Adjustment',category:'pretreatment',shortLabel:'pH Adj.'},
  {key:'buffer',label:'Buffer Tank',category:'pretreatment',shortLabel:'Buffer'},
  {key:'daf',label:'DAF Unit',category:'primary',shortLabel:'DAF'},
  {key:'lamella',label:'Lamella Clarifier',category:'primary',shortLabel:'Lamella'},
  {key:'aeration',label:'Aeration Tank',category:'secondary',shortLabel:'Aeration'},
  {key:'secondary',label:'Secondary Clarifier',category:'secondary',shortLabel:'2° Clarif.'},
  {key:'multimedia',label:'Multimedia Filter',category:'tertiary',shortLabel:'MMF'},
  {key:'gac',label:'GAC Filter',category:'tertiary',shortLabel:'GAC'},
  {key:'sludge',label:'Sludge Holding',category:'sludge',shortLabel:'Sludge'},
];
const eq = (...keys: string[]) => keys.map(k => EQ.find(e => e.key === k)!);

const DEMO_TRAINS: Train[] = [
  { id:9001, userId:1, customerId:1001, name:'Windsor Dairy — Full Verified Train',         description:'Nine-stage verified dairy treatment. DAF + extended aeration achieving COD<125, BOD<25, TSS<35 mg/L (UK EA).', sampleId:6001, equipmentList:eq('screening','eq','ph','buffer','daf','aeration','secondary','multimedia','sludge'), status:'assessed', assessmentResult:null, createdAt:'2026-01-20T14:00:00Z', updatedAt:'2026-01-20T14:00:00Z', _local:true, _demo:true },
  { id:9002, userId:1, customerId:1003, name:'Sheffield Metalworks — Hydroxide Precipitation', description:'Five-stage verified metal removal. Alkali dosing precipitates Pb/Cr as hydroxides. Achieves Pb<0.1 and Cr<0.1 mg/L.', sampleId:6007, equipmentList:eq('ph','eq','lamella','multimedia','sludge'), status:'assessed', assessmentResult:null, createdAt:'2026-01-30T10:00:00Z', updatedAt:'2026-01-30T10:00:00Z', _local:true, _demo:true },
  { id:9003, userId:1, customerId:1006, name:'Sydney Meats — Abattoir Full Treatment',      description:'Seven-stage abattoir treatment. DAF removes blood proteins and FOG. Biological stage achieves AU EPA Cat 2 limits (COD<300, BOD<50, TSS<60 mg/L).', sampleId:6016, equipmentList:eq('screening','eq','ph','daf','aeration','secondary','sludge'), status:'assessed', assessmentResult:null, createdAt:'2026-01-10T15:00:00Z', updatedAt:'2026-01-10T15:00:00Z', _local:true, _demo:true },
  { id:9004, userId:1, customerId:1011, name:'Glasgow Single Malt — High-Strength Distillery', description:'Eight-stage verified train for pot ale (COD 18,000-25,000 mg/L). Extended aeration with GAC polishing achieves UK EA discharge consent.', sampleId:6031, equipmentList:eq('eq','ph','buffer','daf','aeration','secondary','gac','sludge'), status:'assessed', assessmentResult:null, createdAt:'2026-01-16T12:00:00Z', updatedAt:'2026-01-16T12:00:00Z', _local:true, _demo:true },
  { id:9005, userId:1, customerId:1013, name:'Perth Mining — AMD Neutralisation Train',     description:'Seven-stage AMD treatment. Lime dosing raises pH from 2.7 to 9.0, precipitating Fe/Mn/Cu/Zn as hydroxides. Achieves AU EPA metal limits.', sampleId:6037, equipmentList:eq('ph','eq','buffer','lamella','multimedia','gac','sludge'), status:'assessed', assessmentResult:null, createdAt:'2026-01-18T14:00:00Z', updatedAt:'2026-01-18T14:00:00Z', _local:true, _demo:true },
  { id:9006, userId:1, customerId:1020, name:'East London WW — Municipal AS Train',         description:'Eight-stage municipal WwTW. Conventional activated sludge + tertiary sand filtration meets UWWTD and UK EA river quality standards (COD<40, BOD<10).', sampleId:6058, equipmentList:eq('screening','eq','daf','aeration','secondary','multimedia','gac','sludge'), status:'assessed', assessmentResult:null, createdAt:'2026-01-15T16:00:00Z', updatedAt:'2026-01-15T16:00:00Z', _local:true, _demo:true },
];

const DEMO_IDS = new Set(DEMO_CUSTOMERS.map(c => c.id));

// ─── Counts helper ─────────────────────────────────────────────────────────────
function getCounts() {
  const customers = rd<Customer>(LS.customers);
  const samples   = rd<Sample>(LS.influent);
  const trains    = rd<Train>(LS.trains);
  const proposals = rd<any>(LS.proposals);
  return {
    customers: customers.length,
    demoCustomers: customers.filter(c => c.isDemo || DEMO_IDS.has(c.id)).length,
    samples: samples.length,
    demoSamples: samples.filter(s => (s as any)._demo).length,
    trains: trains.length,
    demoTrains: trains.filter(t => (t as any)._demo).length,
    assessed: trains.filter(t => t.status === 'assessed').length,
    proposals: proposals.length,
  };
}

// ─── Confirm Modal ─────────────────────────────────────────────────────────────
function ConfirmModal({ title, body, confirmLabel, danger, onConfirm, onCancel }:{
  title: string; body: string; confirmLabel: string; danger?: boolean;
  onConfirm: () => void; onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[300] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <motion.div initial={{opacity:0,scale:0.95}} animate={{opacity:1,scale:1}}
        className="w-full max-w-md overflow-hidden rb-section">
        <div className={`px-5 py-4 border-b border-slate-800/50 flex items-center gap-3 ${danger ? 'bg-rose-500/5' : 'bg-amber-500/5'}`}>
          <AlertTriangle className={`w-5 h-5 ${danger ? 'text-rose-400' : 'text-amber-400'}`} />
          <h3 className="font-bold text-slate-100 text-sm">{title}</h3>
        </div>
        <div className="p-5">
          <p className="text-slate-400 text-sm leading-relaxed">{body}</p>
        </div>
        <div className="px-5 pb-5 flex gap-3 justify-end">
          <button onClick={onCancel} className="px-4 py-2 rounded-lg text-sm text-slate-400 hover:text-white hover:bg-slate-800 transition-colors">Cancel</button>
          <button onClick={onConfirm}
            className={`px-5 py-2 rounded-lg text-sm font-bold text-white transition-colors ${danger ? 'bg-rose-600 hover:bg-rose-500' : 'bg-amber-600 hover:bg-amber-500'}`}>
            {confirmLabel}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
export default function DemoDataAdminPage() {
  const navigate = useNavigate();
  const [counts, setCounts] = useState(getCounts());
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [samples, setSamples] = useState<Sample[]>([]);
  const [trains, setTrains] = useState<Train[]>([]);
  const [modal, setModal] = useState<null | 'loadDemo' | 'clearDemo' | 'clearAll' | 'dbClearAll' | { type:'deleteCustomer'; id:string; company:string } | { type:'dbImport'; slug:string; datasetName:string }>(null);
  const [loading, setLoading] = useState(false);

  // ── DB operations state ───────────────────────────────────────────────────
  const [dbDatasets, setDbDatasets] = useState<DbDataset[]>([]);
  const [dbLoadingDatasets, setDbLoadingDatasets] = useState(false);
  const [dbOperating, setDbOperating] = useState<string | false>(false);
  const [tasmanOperating, setTasmanOperating] = useState(false);

  // ── DB-side aggregate counts (replaces localStorage counts when authenticated) ─
  const [dbCounts, setDbCounts] = useState<null | {
    customers: number; demoCustomers: number;
    samples: number;   demoSamples: number;
    trains: number;    demoTrains: number;
    assessed: number;  proposals: number;
  }>(null);

  // ── Auth token helper — MUST read rb_api_token (same key phpApi.ts uses) ──
  const getToken = () => localStorage.getItem('rb_api_token') || '';

  // ── Nuke ALL data from localStorage ──────────────────────────────────────
  // Preserves auth keys only: rb_api_token, rb_api_url, rb_php_backend
  const nukeLocalStorage = () => {
    // ── Fixed known data keys ─────────────────────────────────────────────
    const DATA_KEYS = [
      // customers
      'rb_customers',
      'rb_customer_sites',
      'rb_selected_customer_id',
      'rb_target_subtab',
      // samples (all variants)
      'rb_influent_samples_v2',
      'rb_influent_samples',
      'rb_customer_samples',
      'rb_samples',
      // process trains (all variants)
      'rb_process_trains_v2',
      'rb_process_trains',
      'rb_train_draft_v1',
      // proposals
      'rb_proposals_v2',
      'rb_proposals',
      'rb_active_proposal_v1',
      'rb_proposal_seq_v1',
      // assessments / reports
      'rb_local_assessments',
      'rb_assessment_session_v1',
      'rb_sample_reports',
      'rb_saved_reports',
      'rb_reports',
      // activity / misc data
      'rb_activity_log_v1',
      'rb_user_activity',
      // dosing / spec configs
      'rb_dosing_config_v2',
      'rb_daf_operating_params',
      'rb_mm_spec',
      'rb_ph_spec',
      'rb_sludge_spec',
      // ui state
      'rb_active_standard',
    ];
    DATA_KEYS.forEach(k => { try { localStorage.removeItem(k); } catch { /* ignore */ } });

    // ── Dynamic per-customer keys  rb_process_train_<id> ─────────────────
    // Must iterate all keys since IDs are not known at compile time
    try {
      const toRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && /^rb_process_train_\d+$/.test(k)) toRemove.push(k);
      }
      toRemove.forEach(k => localStorage.removeItem(k));
    } catch { /* ignore */ }

    // ── Session: strip customer context, preserve auth token ─────────────
    try {
      const sess = JSON.parse(localStorage.getItem('rb_session_v2') || '{}');
      delete sess.currentCustomer;
      delete sess.activeStandard;
      delete sess.currentSample;
      delete sess.activeTrain;
      localStorage.setItem('rb_session_v2', JSON.stringify(sess));
    } catch { /* ignore */ }
  };

  // ── Fetch DB aggregate counts ─────────────────────────────────────────────
  const fetchDbStats = useCallback(async () => {
    if (!hasPhpBackend()) return;
    try {
      const t = getToken();
      const res = await fetch(`${getApiBase()}/admin/stats.php`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      const data = await res.json();
      if (data.customers !== undefined) setDbCounts(data);
    } catch { /* non-fatal — falls back to localStorage counts */ }
  }, []);

  // ── Fetch available DB datasets ───────────────────────────────────────────
  const fetchDbDatasets = useCallback(async () => {
    if (!hasPhpBackend()) return;
    setDbLoadingDatasets(true);
    try {
      // Pass token in URL query param as fallback — Apache on shared hosting
      // often strips the Authorization header via mod_rewrite.
      const t = getToken();
      const res = await fetch(`${getApiBase()}/admin/demo-datasets.php?_token=${encodeURIComponent(t)}`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      const data = await res.json();
      if (data.datasets) setDbDatasets(data.datasets);
    } catch (e) {
      console.warn('Failed to fetch DB datasets:', e);
    } finally {
      setDbLoadingDatasets(false);
    }
  }, []);

  // ── Clear database ────────────────────────────────────────────────────────
  const clearDbData = async () => {
    setDbOperating('clearing');
    try {
      const t = getToken();
      const res = await fetch(`${getApiBase()}/admin/clear-data.php`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ __token: t }),
      });
      const data = await res.json();
      if (data.success) {
        nukeLocalStorage();
        toast.success(`Database cleared — ${data.deletedRows} rows removed. Reloading…`);
        setTimeout(() => { window.location.href = '/admin/demo-data'; }, 1200);
      } else {
        toast.error(data.error || 'Failed to clear database');
        setDbOperating(false);
        setModal(null);
      }
    } catch (e: any) {
      toast.error('Network error: ' + e.message);
      setDbOperating(false);
      setModal(null);
    }
  };

  // ── Import demo dataset to DB ─────────────────────────────────────────────
  const importDemoToDb = async (slug: string) => {
    setDbOperating(slug);
    try {
      const t = getToken();
      const res = await fetch(`${getApiBase()}/admin/import-demo.php`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug, __token: t }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message || 'Demo data imported to database');
        fetchDbDatasets();
        fetchDbStats();
      } else {
        toast.error(data.error || 'Import failed');
      }
    } catch (e: any) {
      toast.error('Network error: ' + e.message);
    } finally {
      setDbOperating(false);
      setModal(null);
    }
  };

  // ── Recreate Tasman Water Technologies demo account ───────────────────────
  const createTasmanDemo = async () => {
    setTasmanOperating(true);
    try {
      const result = await apiAdminCreateTasmanDemo();
      if (result.success) {
        toast.success(
          `Tasman Water Technologies account created — ${result.siteIds?.length ?? 0} sites, ${result.sampleIds?.length ?? 0} influent samples`,
        );
        fetchDbStats();
      } else {
        toast.error(result.message || 'Failed to create Tasman demo account');
      }
    } catch (e: any) {
      toast.error('Error: ' + e.message);
    } finally {
      setTasmanOperating(false);
    }
  };

  const refresh = useCallback(() => {
    setCounts(getCounts());
    setCustomers(rd<Customer>(LS.customers));
    setSamples(rd<Sample>(LS.influent));
    setTrains(rd<Train>(LS.trains));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { fetchDbDatasets(); fetchDbStats(); }, [fetchDbDatasets, fetchDbStats]);

  // ── Actions ────────────────────────────────────────────────────────────────
  const loadDemoData = () => {
    setLoading(true);
    try {
      // Customers — skip IDs that already exist
      const existing = rd<Customer>(LS.customers);
      const existingIds = new Set(existing.map(c => c.id));
      const toAdd = DEMO_CUSTOMERS.filter(c => !existingIds.has(c.id));
      wr(LS.customers, [...existing, ...toAdd]);

      // Samples
      const existingSamples = rd<Sample>(LS.influent);
      const existingSampleIds = new Set(existingSamples.map(s => s.id));
      const samplesToAdd = DEMO_SAMPLES.filter(s => !existingSampleIds.has(s.id));
      wr(LS.influent, [...existingSamples, ...samplesToAdd]);
      // Mirror to legacy key
      const legacySamples = rd<Sample>(LS.legacy);
      const legacyIds = new Set(legacySamples.map(s => s.id));
      const legacyToAdd = DEMO_SAMPLES.filter(s => !legacyIds.has(s.id));
      wr(LS.legacy, [...legacySamples, ...legacyToAdd]);

      // Trains
      const existingTrains = rd<Train>(LS.trains);
      const existingTrainIds = new Set(existingTrains.map(t => t.id));
      const trainsToAdd = DEMO_TRAINS.filter(t => !existingTrainIds.has(t.id));
      wr(LS.trains, [...existingTrains, ...trainsToAdd]);

      toast.success(`Demo data loaded — ${toAdd.length} customers, ${samplesToAdd.length} samples, ${trainsToAdd.length} trains added`);
      refresh();
    } catch (e: any) {
      toast.error('Failed to load demo data: ' + e.message);
    } finally {
      setLoading(false);
      setModal(null);
    }
  };

  const clearDemoData = async () => {
    setLoading(true);
    try {
      // ── If PHP backend is live, delete demo rows from MySQL too ──────────────
      // Without this, CustomersPage re-fetches demo customers from MySQL on mount
      if (hasPhpBackend()) {
        try {
          const t = getToken();
          const res = await fetch(`${getApiBase()}/admin/delete-demo.php`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ __token: t }),
          });
          const data = await res.json();
          if (!data.success) {
            toast.error(data.error || 'Failed to remove demo data from database — localStorage cleared anyway');
          }
        } catch {
          toast.error('Could not reach server — localStorage cleared anyway');
        }
      }
      // ── Always clear localStorage demo data ──────────────────────────────────
      const demoCustomerIds = new Set(DEMO_CUSTOMERS.map(c => c.id));
      wr(LS.customers, rd<Customer>(LS.customers).filter(c => !c.isDemo && !demoCustomerIds.has(c.id)));
      wr(LS.influent,  rd<Sample>(LS.influent).filter(s => !(s as any)._demo));
      wr(LS.legacy,    rd<Sample>(LS.legacy).filter(s => !(s as any)._demo));
      wr(LS.trains,    rd<Train>(LS.trains).filter(t => !(t as any)._demo));
      wr(LS.proposals, rd<any>(LS.proposals).filter(p => {
        const isDemoCustomer = demoCustomerIds.has(String(p.customerId));
        return !isDemoCustomer && !p._demo;
      }));
      // Clear session if active customer was a demo customer
      try {
        const sess = JSON.parse(localStorage.getItem(LS.session) || '{}');
        if (sess.currentCustomer && demoCustomerIds.has(sess.currentCustomer.id)) {
          delete sess.currentCustomer;
          delete sess.activeStandard;
          localStorage.setItem(LS.session, JSON.stringify(sess));
          localStorage.removeItem('rb_active_standard');
        }
      } catch { /* ignore */ }
      toast.success('Demo data cleared — reloading…');
      setTimeout(() => { window.location.href = '/admin'; }, 700);
    } catch (e: any) {
      toast.error('Error: ' + e.message);
      setLoading(false);
      setModal(null);
    }
  };

  const clearAllData = async () => {
    setLoading(true);
    try {
      // ── When PHP backend is live, wipe MySQL FIRST ────────────────────────
      // CustomersPage calls apiListCustomers() on every mount — if MySQL isn't
      // cleared, data reappears immediately after localStorage is nuked.
      if (hasPhpBackend()) {
        let phpOk = false;
        try {
          const t = getToken();
          const res = await fetch(`${getApiBase()}/admin/clear-data.php`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ __token: t }),
          });
          const data = await res.json();
          if (data.success) {
            phpOk = true;
          } else {
            // Surface the real error — do NOT silently proceed
            toast.error(`Database clear failed: ${data.message || data.error || 'Unknown error'}. Check DB user has DELETE privilege.`);
            setLoading(false);
            setModal(null);
            return; // ← stop here; don't clear localStorage or redirect
          }
        } catch (netErr: any) {
          toast.error(`Could not reach server: ${netErr.message}. Check API is running.`);
          setLoading(false);
          setModal(null);
          return; // ← stop here
        }
        if (!phpOk) return;
      }

      nukeLocalStorage();
      toast.success('All data cleared — reloading…');
      setTimeout(() => { window.location.href = '/'; }, 900);
    } catch (e: any) {
      toast.error('Error: ' + e.message);
      setLoading(false);
      setModal(null);
    }
  };

  const deleteCustomer = (id: string) => {
    const cid = parseInt(id);
    wr(LS.customers, rd<Customer>(LS.customers).filter(c => c.id !== id));
    wr(LS.influent,  rd<Sample>(LS.influent).filter(s => s.customerId !== cid));
    wr(LS.legacy,    rd<Sample>(LS.legacy).filter(s => s.customerId !== cid));
    wr(LS.trains,    rd<Train>(LS.trains).filter(t => t.customerId !== cid));
    wr(LS.proposals, rd<any>(LS.proposals).filter(p => p.customerId !== id && p.customerId !== cid));
    // Clear active proposal if it belonged to this customer
    try {
      const ap = JSON.parse(localStorage.getItem('rb_active_proposal_v1') || 'null');
      if (ap && (ap.customerId === id || ap.customerId === cid)) {
        localStorage.removeItem('rb_active_proposal_v1');
      }
    } catch { /* ignore */ }
    // Clear active customer if it was this one, also clear active standard
    try {
      const sess = JSON.parse(localStorage.getItem(LS.session) || '{}');
      if (sess.currentCustomer?.id === id) {
        delete sess.currentCustomer;
        delete sess.activeStandard;
        localStorage.setItem(LS.session, JSON.stringify(sess));
        localStorage.removeItem('rb_active_standard');
      }
    } catch { /* ignore */ }
    toast.success('Customer and all their data deleted');
    // Reload so React contexts reinitialize from updated localStorage
    setTimeout(() => { window.location.href = '/admin'; }, 700);
  };

  // ── Per-customer stats ────────────────────────────────────────────────────
  function custStats(c: Customer) {
    const cid = parseInt(c.id);
    const s = samples.filter(x => x.customerId === cid);
    const t = trains.filter(x => x.customerId === cid);
    return {
      influent: s.filter(x => x.sampleType === 'influent').length,
      effluent: s.filter(x => x.sampleType === 'effluent').length,
      trains: t.length,
      assessed: t.filter(x => x.status === 'assessed').length,
    };
  }

  const demoCustomerIds = new Set(DEMO_CUSTOMERS.map(c => c.id));
  const demoLoaded = counts.demoCustomers > 0;

  return (
    <div className="space-y-8">
      <PageHeader
        icon={Database}
        title="Demo Data Manager"
        subtitle="Load, inspect, and clear demo customers, samples, and process trains"
        actions={
          <button onClick={() => navigate('/admin')} className="flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-bold bg-slate-800/80 border border-slate-800/60 text-slate-300 hover:bg-slate-700 hover:border-slate-600 transition-all">
            <ArrowLeft className="w-3.5 h-3.5" /> Admin
          </button>
        }
      />

        {/* ── Stats ─────────────────────────────────────────────────────────── */}
        {/* When authenticated dbCounts is live data from MySQL; else falls back to localStorage counts */}
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            {dbCounts ? (
              <span className="flex items-center gap-1.5 text-[10px] font-semibold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full">
                <Database className="w-3 h-3" /> Live from database
              </span>
            ) : (
              <span className="text-[10px] text-slate-500">from localStorage</span>
            )}
          </div>
          <button onClick={() => { refresh(); fetchDbStats(); }}
            className="text-[10px] text-slate-500 hover:text-slate-300 flex items-center gap-1 transition-colors">
            <RefreshCw className="w-3 h-3" /> Refresh counts
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {(() => {
            const c = dbCounts ?? counts;
            const isDb = !!dbCounts;
            return [
              { label:'Total Customers',  value:c.customers,  sub:`${c.demoCustomers} demo`,    icon:Users,        color:'text-rb-brand',  bg:'bg-rb-brand/10'  },
              { label:'Total Samples',    value:c.samples,    sub:`${c.demoSamples} demo`,      icon:FlaskConical, color:'text-cyan-400',   bg:'bg-cyan-500/10'   },
              { label:'Process Trains',   value:c.trains,     sub:`${c.assessed} assessed`,     icon:Workflow,     color:'text-amber-400',  bg:'bg-amber-500/10'  },
              { label:'Proposals',        value:c.proposals,  sub: isDb ? 'in database' : 'in localStorage', icon:BarChart3, color:'text-violet-400', bg:'bg-violet-500/10' },
            ];
          })().map(s => (
            <div key={s.label} className="p-4 rb-card">
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-lg ${s.bg} flex items-center justify-center`}>
                  <s.icon className={`w-4 h-4 ${s.color}`} />
                </div>
                <div>
                  <div className={`text-2xl font-black ${s.color}`}>{s.value}</div>
                  <div className="text-[10px] text-slate-500 font-medium">{s.label}</div>
                  <div className="text-[10px] text-slate-600">{s.sub}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Actions ───────────────────────────────────────────────────────── */}
        <div className="p-6 rb-section">
          <div className="flex items-center gap-2 mb-5">
            <Zap className="w-4 h-4 text-rb-brand" />
            <h2 className="text-sm font-bold text-slate-200">Data Actions</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

            {/* Load Demo */}
            <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Star className="w-4 h-4 text-emerald-400" />
                <span className="text-sm font-bold text-emerald-400">Load Demo Data</span>
              </div>
              <p className="text-[11px] text-slate-400 mb-4 leading-relaxed">
                Adds 20 demo customers, 60 scientifically accurate samples (influent + effluent), and 6 validated process trains to localStorage. Existing records are preserved (no duplicates).
              </p>
              {demoLoaded && (
                <div className="flex items-center gap-1.5 mb-3 text-[10px] text-emerald-400">
                  <CheckCircle2 className="w-3 h-3" />
                  {counts.demoCustomers} demo customers already loaded
                </div>
              )}
              <button onClick={() => setModal('loadDemo')} disabled={loading}
                className="w-full py-2.5 rounded-lg text-sm font-bold bg-emerald-600 hover:bg-emerald-500 text-white transition-colors disabled:opacity-40">
                {loading ? 'Loading…' : 'Load Demo Data'}
              </button>
            </div>

            {/* Clear Demo */}
            <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
              <div className="flex items-center gap-2 mb-2">
                <RefreshCw className="w-4 h-4 text-amber-400" />
                <span className="text-sm font-bold text-amber-400">Clear Demo Data</span>
              </div>
              <p className="text-[11px] text-slate-400 mb-4 leading-relaxed">
                Removes only demo-flagged customers, samples, and trains. Your real client data is left untouched.
              </p>
              <button onClick={() => setModal('clearDemo')} disabled={loading || !demoLoaded}
                className="w-full py-2.5 rounded-lg text-sm font-bold bg-amber-600 hover:bg-amber-500 text-white transition-colors disabled:opacity-40">
                Clear Demo Data
              </button>
            </div>

            {/* Clear ALL */}
            <div className="rounded-xl border border-rose-500/20 bg-rose-500/5 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Trash2 className="w-4 h-4 text-rose-400" />
                <span className="text-sm font-bold text-rose-400">Clear ALL Customer Data</span>
              </div>
              <p className="text-[11px] text-slate-400 mb-4 leading-relaxed">
                {hasPhpBackend()
                  ? 'Permanently removes all customers, samples, trains, and proposals from both the MySQL database and localStorage. Fresh start — no data remains.'
                  : 'Permanently removes all customers, samples, trains, and proposals from localStorage. This cannot be undone.'}
              </p>
              {hasPhpBackend() && (
                <div className="flex items-center gap-1.5 mb-3 text-[10px] text-rose-400/70">
                  <HardDrive className="w-3 h-3" />
                  Also wipes MySQL database
                </div>
              )}
              <button onClick={() => setModal('clearAll')} disabled={loading}
                className="w-full py-2.5 rounded-lg text-sm font-bold bg-rose-700 hover:bg-rose-600 text-white transition-colors disabled:opacity-40">
                {loading ? 'Clearing…' : 'Clear Everything'}
              </button>
            </div>
          </div>
        </div>

        {/* ── Database Operations ───────────────────────────────────────────── */}
        <div className="overflow-hidden rb-section">
          <div className="px-6 py-4 border-b border-slate-800/60 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <HardDrive className="w-4 h-4 text-violet-400" />
              <h2 className="text-sm font-bold text-slate-200">Database Operations</h2>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-violet-500/10 text-violet-400 border border-violet-500/20">SERVER</span>
            </div>
            {hasPhpBackend() && (
              <button onClick={fetchDbDatasets} disabled={dbLoadingDatasets}
                className="flex items-center gap-1.5 text-[11px] text-slate-500 hover:text-slate-300 transition-colors disabled:opacity-40">
                <RefreshCw className={`w-3 h-3 ${dbLoadingDatasets ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            )}
          </div>

          {!hasPhpBackend() ? (
            <div className="p-6">
              <div className="flex items-start gap-3 p-4 rounded-xl border border-slate-800/50 bg-slate-800/30">
                <ServerCrash className="w-5 h-5 text-slate-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-slate-400">PHP Backend Not Detected</p>
                  <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                    Database import and clear operations require the PHP API backend. These features are
                    available when the app is deployed with the <code className="text-slate-400 bg-slate-800 px-1 rounded">deploy/api</code> layer.
                    Use the localStorage actions above in standalone / demo mode.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-6 space-y-6">

              {/* ── Import Datasets Table ────────────────────────────────────── */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Upload className="w-3.5 h-3.5 text-violet-400" />
                  <h3 className="text-xs font-bold text-slate-300">Available Demo Datasets</h3>
                  <span className="text-[10px] text-slate-600">— click Import to seed the MySQL database</span>
                </div>

                {dbLoadingDatasets ? (
                  <div className="py-8 flex items-center justify-center gap-2 text-slate-500 text-sm">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Loading dataset registry…
                  </div>
                ) : dbDatasets.length === 0 ? (
                  <div className="py-8 text-center">
                    <Database className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                    <p className="text-slate-500 text-sm">No datasets returned from server</p>
                    <p className="text-slate-600 text-xs mt-1">Check that <code className="text-slate-500">api/admin/demo-datasets.php</code> is accessible</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-xl border border-slate-800/50">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-800/50 bg-slate-800/40 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                          <th className="px-4 py-3 text-left">Dataset</th>
                          <th className="px-4 py-3 text-left">Region / Standard</th>
                          <th className="px-4 py-3 text-center">Customers</th>
                          <th className="px-4 py-3 text-center">Samples</th>
                          <th className="px-4 py-3 text-center">Trains</th>
                          <th className="px-4 py-3 text-center">File</th>
                          <th className="px-4 py-3 text-left">Last Imported</th>
                          <th className="px-4 py-3 text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dbDatasets.map((ds, i) => {
                          const isImporting = dbOperating === ds.slug;
                          const hasBeenImported = (ds.importedCount ?? 0) > 0;
                          return (
                            <motion.tr key={ds.slug}
                              initial={{opacity:0,y:4}} animate={{opacity:1,y:0}} transition={{delay:i*0.05}}
                              className="border-b border-slate-800/40 hover:bg-slate-800/20 transition-colors">
                              <td className="px-4 py-3">
                                <div className="font-bold text-slate-200 text-xs">{ds.name}</div>
                                <div className="text-[10px] text-slate-500 mt-0.5 max-w-[220px] leading-relaxed">{ds.description}</div>
                              </td>
                              <td className="px-4 py-3">
                                <span className="text-[10px] font-bold text-violet-300">{ds.region}</span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-rb-brand/10 text-rb-brand">{ds.customerCount}</span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-cyan-500/10 text-cyan-400">{ds.sampleCount}</span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-500/10 text-amber-400">{ds.trainCount}</span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                {ds.fileExists
                                  ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mx-auto" />
                                  : <X className="w-3.5 h-3.5 text-rose-500 mx-auto" title="SQL file not found on server" />
                                }
                              </td>
                              <td className="px-4 py-3">
                                {hasBeenImported && ds.lastImportedAt ? (
                                  <div>
                                    <div className="flex items-center gap-1 text-[10px] text-emerald-400">
                                      <Clock className="w-3 h-3" />
                                      {new Date(ds.lastImportedAt).toLocaleDateString('en-GB', {day:'2-digit',month:'short',year:'numeric'})}
                                    </div>
                                    <div className="text-[10px] text-slate-600 mt-0.5">{ds.importedCount}× imported</div>
                                  </div>
                                ) : (
                                  <span className="text-[10px] text-slate-600 italic">Not yet imported</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <button
                                  onClick={() => setModal({ type:'dbImport', slug:ds.slug, datasetName:ds.name })}
                                  disabled={!ds.fileExists || !!dbOperating}
                                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all mx-auto
                                    ${!ds.fileExists
                                      ? 'bg-slate-800 text-slate-600 cursor-not-allowed'
                                      : hasBeenImported
                                        ? 'bg-violet-600/20 text-violet-400 border border-violet-500/30 hover:bg-violet-600/40'
                                        : 'bg-violet-600 text-white hover:bg-violet-500'
                                    } disabled:opacity-40`}>
                                  {isImporting
                                    ? <><RefreshCw className="w-3 h-3 animate-spin" /> Importing…</>
                                    : hasBeenImported
                                      ? <><Download className="w-3 h-3" /> Re-Import</>
                                      : <><Download className="w-3 h-3" /> Import</>
                                  }
                                </button>
                              </td>
                            </motion.tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* ── Tasman Water Technologies Demo Account ───────────────────── */}
              <div className="border-t border-slate-800/60 pt-5">
                <div className="flex items-start justify-between gap-4 p-4 rounded-xl border border-emerald-500/20 bg-emerald-500/5">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0">
                      <Globe className="w-4 h-4 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-emerald-400 flex items-center gap-2">
                        🇦🇺 Tasman Water Technologies Demo
                        <span className="text-[10px] font-normal bg-emerald-500/10 border border-emerald-500/20 px-1.5 py-0.5 rounded text-emerald-300">AU EPA</span>
                      </p>
                      <p className="text-[11px] text-slate-400 mt-1 leading-relaxed max-w-xl">
                        Creates (or resets) the <strong className="text-slate-300">Tasmanwater</strong> user account with 1 customer, 3 sites, and 9 realistic Australian influent samples.
                        Login: <code className="text-emerald-300 bg-slate-800 px-1 rounded">Tasmanwater</code> / <code className="text-emerald-300 bg-slate-800 px-1 rounded">Tasman!</code>.
                        Safe to re-run after clearing — existing account is deleted and recreated fresh.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={createTasmanDemo}
                    disabled={tasmanOperating || !!dbOperating}
                    className="shrink-0 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold bg-emerald-700 hover:bg-emerald-600 text-white transition-colors disabled:opacity-40">
                    {tasmanOperating
                      ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Creating…</>
                      : <><Zap className="w-3.5 h-3.5" /> Create / Reset</>
                    }
                  </button>
                </div>
              </div>

              {/* ── Clear Database ───────────────────────────────────────────── */}
              <div className="border-t border-slate-800/60 pt-5">
                <div className="flex items-start justify-between gap-4 p-4 rounded-xl border border-rose-500/20 bg-rose-500/5">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-rose-500/10 border border-rose-500/20 flex items-center justify-center shrink-0">
                      <ServerCrash className="w-4 h-4 text-rose-400" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-rose-400">Clear Database</p>
                      <p className="text-[11px] text-slate-400 mt-1 leading-relaxed max-w-xl">
                        Truncates all customer-facing tables in MySQL (customers, influent_samples, process_trains, proposals, customer_sites, assessment_results).
                        Preserves users, activity log, and system tables. Also clears localStorage for a clean state.
                        <span className="text-rose-400 font-bold"> This cannot be undone.</span>
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setModal('dbClearAll')}
                    disabled={!!dbOperating}
                    className="shrink-0 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold bg-rose-700 hover:bg-rose-600 text-white transition-colors disabled:opacity-40">
                    {dbOperating === 'clearing'
                      ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Clearing…</>
                      : <><Trash2 className="w-3.5 h-3.5" /> Clear Database</>
                    }
                  </button>
                </div>
              </div>

            </div>
          )}
        </div>

        {/* ── Customer Directory ────────────────────────────────────────────── */}
        <div className="overflow-hidden rb-section">
          <div className="px-6 py-4 border-b border-slate-800/60 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-rb-brand" />
              <h2 className="text-sm font-bold text-slate-200">Customer Directory</h2>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-800 text-slate-400">{customers.length} total</span>
            </div>
            <button onClick={() => navigate('/customers')}
              className="flex items-center gap-1.5 text-[11px] text-rb-brand hover:text-[#e8bc38] transition-colors">
              Open CRM <ChevronRight className="w-3 h-3" />
            </button>
          </div>

          {customers.length === 0 ? (
            <div className="py-16 text-center">
              <Database className="w-12 h-12 text-slate-400 mx-auto mb-3" />
              <p className="text-slate-500 text-sm font-medium">No customers in localStorage</p>
              <p className="text-slate-600 text-xs mt-1">Load demo data to populate the directory</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-800 bg-slate-800/30 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                    <th className="px-4 py-3 text-left">Company</th>
                    <th className="px-4 py-3 text-left">Country / Industry</th>
                    <th className="px-4 py-3 text-left">Standard</th>
                    <th className="px-4 py-3 text-center">Influent</th>
                    <th className="px-4 py-3 text-center">Effluent</th>
                    <th className="px-4 py-3 text-center">Trains</th>
                    <th className="px-4 py-3 text-center">Assessed</th>
                    <th className="px-4 py-3 text-left">Contact</th>
                    <th className="px-4 py-3 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((c, i) => {
                    const isDemo = c.isDemo || demoCustomerIds.has(c.id);
                    const stats = custStats(c);
                    const flag = c.country ? (COUNTRY_FLAGS[c.country] || '🌐') : null;
                    const stdColor = c.standard ? (STANDARD_COLORS[c.standard] || 'text-slate-400') : 'text-slate-400';
                    return (
                      <motion.tr key={c.id}
                        initial={{opacity:0,y:6}} animate={{opacity:1,y:0}} transition={{delay:i*0.02}}
                        className="border-b border-slate-800/40 hover:bg-slate-800/20 transition-colors group">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-rb-brand/10 border border-rb-brand/15 flex items-center justify-center text-xs font-black text-rb-brand">
                              {c.company.charAt(0)}
                            </div>
                            <div>
                              <div className="font-bold text-slate-200 text-xs flex items-center gap-1.5">
                                {c.company}
                                {isDemo && <span className="px-1 py-0.5 rounded text-[10px] font-black bg-rb-brand/15 text-rb-brand border border-rb-brand/20">DEMO</span>}
                              </div>
                              <div className="text-[10px] text-slate-500">{c.name}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5 text-xs text-slate-400">
                            {flag && <span>{flag}</span>}
                            <span className="text-slate-500">{c.industryType || '—'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-[10px] font-bold ${stdColor}`}>{c.standard || '—'}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {stats.influent > 0 ? <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-cyan-500/10 text-cyan-400">{stats.influent}</span> : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {stats.effluent > 0 ? <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-500/10 text-amber-400">{stats.effluent}</span> : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {stats.trains > 0 ? <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-700 text-slate-300">{stats.trains}</span> : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {stats.assessed > 0 ? <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-500/10 text-emerald-400">✓ {stats.assessed}</span> : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-[10px] text-slate-500 truncate max-w-[140px]">{c.contactEmail}</div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => setModal({ type:'deleteCustomer', id:c.id, company:c.company })}
                            className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg text-slate-600 hover:text-rose-400 hover:bg-rose-500/10 transition-all">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Verification ──────────────────────────────────────────────────── */}
        <div className="p-6 rb-section">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-4 h-4 text-rb-brand" />
            <h2 className="text-sm font-bold text-slate-200">Demo Data Verification</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
            {DEMO_CUSTOMERS.map(c => {
              const cid = parseInt(c.id);
              const sampleCount = DEMO_SAMPLES.filter(s => s.customerId === cid).length;
              const trainCount  = DEMO_TRAINS.filter(t => t.customerId === cid).length;
              const flag = c.country ? (COUNTRY_FLAGS[c.country] || '🌐') : '';
              const loaded = customers.some(x => x.id === c.id);
              return (
                <div key={c.id} className={`flex items-center gap-3 p-3 rounded-lg border ${loaded ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-slate-800/50 bg-slate-800/30'}`}>
                  <span className="text-base">{flag}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-slate-200 truncate">{c.company}</div>
                    <div className="text-[10px] text-slate-500">{c.industryType} · {c.standard}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 text-[10px]">
                    <span className="text-cyan-400">{sampleCount}s</span>
                    {trainCount > 0 && <span className="text-amber-400">{trainCount}t</span>}
                    {loaded
                      ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                      : <X className="w-3.5 h-3.5 text-slate-600" />
                    }
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      {/* ── Modals ────────────────────────────────────────────────────────────── */}
      <AnimatePresence>
        {modal === 'loadDemo' && (
          <ConfirmModal
            title="Load Demo Data"
            body={`This will add up to 20 demo customers, 60 samples, and 6 assessed process trains to localStorage. Existing records with matching IDs will be skipped. Currently ${counts.demoCustomers} demo customers are loaded.`}
            confirmLabel="Load Demo Data"
            onConfirm={loadDemoData}
            onCancel={() => setModal(null)}
          />
        )}
        {modal === 'clearDemo' && (
          <ConfirmModal
            title="Clear Demo Data"
            body={`This will remove all ${counts.demoCustomers} demo customers, ${counts.demoSamples} demo samples, and ${counts.demoTrains} demo trains from localStorage. Your real client data will not be affected.`}
            confirmLabel="Clear Demo Data"
            danger
            onConfirm={clearDemoData}
            onCancel={() => setModal(null)}
          />
        )}
        {modal === 'clearAll' && (
          <ConfirmModal
            title="Clear ALL Customer Data"
            body={hasPhpBackend()
              ? `This will permanently delete ALL customers, samples, trains, and proposals from both the MySQL database and localStorage. The app will restart completely fresh. This cannot be undone.`
              : `This will permanently delete ALL ${counts.customers} customers, ${counts.samples} samples, ${counts.trains} trains, and ${counts.proposals} proposals from localStorage. This action cannot be undone.`}
            confirmLabel="Delete Everything"
            danger
            onConfirm={clearAllData}
            onCancel={() => setModal(null)}
          />
        )}
        {modal && typeof modal === 'object' && modal.type === 'deleteCustomer' && (
          <ConfirmModal
            title={`Delete ${modal.company}`}
            body={`This will permanently delete "${modal.company}" and all their associated samples, process trains, and proposals from localStorage. This cannot be undone.`}
            confirmLabel="Delete Customer"
            danger
            onConfirm={() => deleteCustomer(modal.id)}
            onCancel={() => setModal(null)}
          />
        )}
        {modal === 'dbClearAll' && (
          <ConfirmModal
            title="Clear MySQL Database"
            body="This will truncate all customer-facing tables in the MySQL database — customers, samples, process trains, proposals, sites, and assessment results. User accounts and system tables are preserved. localStorage will also be cleared. This cannot be undone."
            confirmLabel="Clear Database"
            danger
            onConfirm={() => { setModal(null); clearDbData(); }}
            onCancel={() => setModal(null)}
          />
        )}
        {modal && typeof modal === 'object' && modal.type === 'dbImport' && (
          <ConfirmModal
            title={`Import: ${modal.datasetName}`}
            body={`This will import the "${modal.datasetName}" dataset into the MySQL database using INSERT IGNORE — existing records with matching IDs will be skipped safely. If data was previously imported, it will not be duplicated. Continue?`}
            confirmLabel="Import Dataset"
            onConfirm={() => { setModal(null); importDemoToDb(modal.slug); }}
            onCancel={() => setModal(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
